import type { Constraint, ConstraintResult } from '@/types'

export function runConstraintEngine(
  selectedModelId: number | null,
  selectedOptionIds: number[],
  constraints: Constraint[]
): ConstraintResult {
  const warnings: ConstraintResult['warnings'] = []
  const blockers: ConstraintResult['blockers'] = []
  const autoAdd: number[] = []
  const autoRemove: number[] = []
  const disabled: number[] = []

  for (const c of constraints) {
    if (!c.is_active) continue
    if (c.source_type !== 'option') continue

    const sourceSelected = selectedOptionIds.includes(c.source_id)

    switch (c.constraint_type) {
      case 'excludes': {
        if (sourceSelected) {
          disabled.push(c.target_id)
          if (selectedOptionIds.includes(c.target_id)) {
            autoRemove.push(c.target_id)
          }
        }
        break
      }

      case 'requires': {
        if (sourceSelected && !selectedOptionIds.includes(c.target_id)) {
          if (c.severity === 'blocking') {
            autoAdd.push(c.target_id)
          } else {
            warnings.push({ message: c.message, optionId: c.source_id })
          }
        }
        break
      }

      case 'incompatible_with_model': {
        if (sourceSelected && c.target_type === 'model' && c.target_id === selectedModelId) {
          if (c.severity === 'blocking') {
            blockers.push({ message: c.message, optionId: c.source_id })
          } else {
            warnings.push({ message: c.message, optionId: c.source_id })
          }
        }
        break
      }

      case 'warning_only': {
        if (sourceSelected) {
          const targetMatches =
            c.target_type === 'model'
              ? c.target_id === selectedModelId
              : selectedOptionIds.includes(c.target_id) || c.target_id === c.source_id
          if (targetMatches) {
            warnings.push({ message: c.message, optionId: c.source_id })
          }
        }
        break
      }
    }
  }

  return {
    warnings,
    blockers,
    autoAdd: [...new Set(autoAdd)],
    autoRemove: [...new Set(autoRemove)],
    disabled: [...new Set(disabled)],
  }
}

export function resolveOptions(
  currentIds: number[],
  toggled: number,
  constraints: Constraint[],
  selectedModelId: number | null
): number[] {
  let next = currentIds.includes(toggled)
    ? currentIds.filter((id) => id !== toggled)
    : [...currentIds, toggled]

  const result = runConstraintEngine(selectedModelId, next, constraints)

  // Apply auto-removals
  next = next.filter((id) => !result.autoRemove.includes(id))

  // Apply auto-adds
  for (const id of result.autoAdd) {
    if (!next.includes(id)) next.push(id)
  }

  return next
}
